import React, { useCallback, useState } from 'react';
import { Button, Form, Input, Modal, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useDataProvider, useListContext, useNotify } from 'react-admin';
import PaginatedSelect, { PAGE_SIZE } from '@src/components/Common/PaginatedSelect';
import { ExtendedDataProvider } from '@src/types';

const { Option } = Select;

/** the roles a platform admin may hand out from here */
export const ASSIGNABLE_ROLES = ['ADMIN', 'GENERAL', 'READ_ONLY'];

/** the endpoint's own default, so the form opens on the least it can grant */
const DEFAULT_ROLE = 'READ_ONLY';

/**
 * Put an existing user on a team — the button and the form behind it.
 *
 * Self-contained on purpose: it is handed the team to open on and otherwise
 * asks for what it needs itself, so the list it sits above does not have to
 * carry the form's state, its team list or its errors. Drop it in a
 * `headerTrailing` and it works.
 *
 * The user is named rather than picked from a list. That is the endpoint's
 * design and not a shortcut here: the user table is deliberately unbrowsable,
 * so an exact username or email leaks no more than the invitation flow does.
 * A name that matches nothing comes back as an error on the field.
 *
 * Adding to any team is a platform admin's to do — the API gates `add_member`
 * on `is_staff` — so this belongs to the admin point of view alone.
 */
const NewMemberButton = ({
    teamId,
    /** the name of the team the form opens on, so it reads before its page loads */
    teamName,
    /** what the endpoint the list reads is called; the two share a resource */
    resource,
}: {
    /** the team the page is about, which the form opens on */
    teamId?: number;
    teamName?: string;
    resource: string;
}) => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    // the list this sits above, re-read through its own context rather than
    // through the global refresh -- see the note on MembersView
    const { refetch } = useListContext();

    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    /** the name behind the team the picker is on, for the confirmation */
    const [chosenName, setChosenName] = useState<string | undefined>(teamName);
    const [form] = Form.useForm();

    /* One page of the team picker, searched by name. A deployment holds more
       teams than a dropdown should load at once, and asking for a big page
       instead just moves the cliff: whatever the API's cap is, the teams past
       it are silently missing and the operator has no way to tell. */
    const fetchTeamsPage = useCallback(
        (search: string, page: number) =>
            dataProvider
                .getList('all_teams', {
                    pagination: { page, perPage: PAGE_SIZE },
                    sort: { field: 'name', order: 'ASC' },
                    filter: { name: search },
                })
                .then(({ data, total }: any) => ({
                    options: data.map((team: any) => ({ value: team.id, label: team.name })),
                    more: page * PAGE_SIZE < (total ?? 0),
                })),
        [dataProvider],
    );

    const start = () => {
        form.setFieldsValue({ team_id: teamId, role: DEFAULT_ROLE, identifier: '' });
        setOpen(true);
    };

    /**
     * What went wrong, in the words the API used.
     *
     * The serializer answers on the field that was wrong — an identifier that
     * matches nobody, a team that is gone, a user already on the team — and
     * those are the sentences worth showing. A generic "could not add" would
     * throw away the only part of the response that helps.
     */
    const onError = (error: any) => {
        const body = error?.body;
        const message = body?.identifier || body?.team_id || body?.role || body?.detail
            || error?.message || 'Could not add the member.';
        notify(Array.isArray(message) ? message[0] : String(message), 'warning');
    };

    const submit = (values: any) => {
        setSaving(true);
        dataProvider
            .createAction(resource, {
                verb: 'add_member/',
                data: { ...values, identifier: (values.identifier || '').trim() },
            })
            .then(() => {
                // named from what the picker showed, since only one page of
                // teams is ever loaded and the rest are not here to look up
                const team = values.team_id === teamId ? teamName : chosenName;
                notify(`${values.identifier} added${team ? ` to ${team}` : ''}.`);
                setOpen(false);
                form.resetFields();
                refetch();
            })
            .catch(onError)
            .finally(() => setSaving(false));
    };

    return (
        <>
            <Button
                className="tm-head-action"
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                data-cy="new-member"
                onClick={start}
            >
                New member
            </Button>

            <Modal
                title="New member"
                open={open}
                onCancel={() => setOpen(false)}
                confirmLoading={saving}
                okText="Add"
                onOk={form.submit}
                destroyOnHidden
            >
                <Form form={form} name="new_member" layout="vertical" onFinish={submit}>
                    <Form.Item
                        name="identifier"
                        label="User"
                        rules={[{ required: true, message: 'Give a username or email address' }]}
                        help="Username or email address, matched exactly."
                    >
                        <Input placeholder="user@example.com" data-cy="new-member-identifier" />
                    </Form.Item>

                    <Form.Item
                        name="role"
                        label="Role"
                        rules={[{ required: true, message: 'Pick a role' }]}
                    >
                        <Select data-cy="new-member-role">
                            {ASSIGNABLE_ROLES.map((role) => (
                                <Option key={role} value={role}>{role}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {/* Opens on the team the page is about, because that is
                        almost always the answer — but it is a choice, so a
                        member can be added without leaving the team in view. */}
                    <Form.Item
                        name="team_id"
                        label="Team"
                        rules={[{ required: true, message: 'Pick a team' }]}
                    >
                        <PaginatedSelect
                            placeholder="Select team"
                            data-cy="new-member-team"
                            /* the team it opens on is listed whatever page that
                               team falls on, so the field is never blank */
                            pinned={teamId ? [{ value: teamId, label: teamName ?? '' }] : []}
                            fetchPage={fetchTeamsPage}
                            onChange={(_value: number, option: any) => setChosenName(option?.label)}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default NewMemberButton;
